import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { DatePicker, TextField, SelectField, Switch } from '../Base/Form'
import SelectLocation from './SelectLocation'
import PromoFields from './PromoFields'
import useResponsiveView from '../../hooks/useResponsiveView'
import { CircularProgress, Modal } from '@mui/material'
import { getData, postData } from '../../../js/helpers/apiHelper'
import LoadingResultsModal from '../Modals/LoadingResultsModal'
import generateTimeOptions from '../../../js/helpers/generateTimeOptions'
import { useDispatch } from 'react-redux'
import { setFilters, setStep } from '../../../js/lib/redux/slices/booking'
import constants from '../../../js/lib/constants'
import ErrorModal from '../Base/ErrorModal'

const now = new Date()
const oneYearLater = new Date()
const OneYearAndMonthLater = new Date()
oneYearLater.setFullYear(now.getFullYear() + 1)
OneYearAndMonthLater.setFullYear(now.getFullYear() + 1)
OneYearAndMonthLater.setMonth(OneYearAndMonthLater.getMonth() + 1)

const BookingWidget = ({
  data,
  handleCancel,
  fleet,
  editMode,
  mobileApp,
  userId,
}) => {
  const {
    dictionary,
    logos,
    driverAgeOptions,
    promoCodeTypeOptions,
    config,
    currentValues,
    // waitForResultsLogos,
  } = data
  const timeOptions = generateTimeOptions()

  const diffLocationInitalCondition =
    currentValues?.pickUpLocation?.code !==
      currentValues?.dropOffLocation?.code ||
    currentValues?.pickUpLocation?.country !==
      currentValues?.dropOffLocation?.country ||
    currentValues?.pickUpLocation?.text !== currentValues?.dropOffLocation?.text

  const [dropOffAtDiffLocation, setDropOffAtDiffLocation] = useState(
    diffLocationInitalCondition
  )
  const [showDiscount, setShowDiscount] = useState(
    currentValues?.promos?.length > 0
  )
  const [isSameDay, setIsSameDay] = useState(false)
  const [isExpanded, setIsExpanded] = useState(
    fleet ? false : editMode || currentValues
  )
  const { isDevice } = useResponsiveView()
  const [openFormModal, setOpenFormModal] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [openLoadingModal, setOpenLoadingModal] = useState(false)
  const [waitForResultsLogos, setWaitForResultsLogos] = useState([])
  const [openErrorModal, setOpenErrorModal] = useState(false)

  const dispatch = !window.mobileAppData ? useDispatch() : null
  const mobileView = isDevice || mobileApp

  const { validation } = dictionary
  const schema = z.object({
    pickUpLocation: z.object(
      {
        text: z.string(),
        value: z.string(),
        country: z.string(),
      },
      { invalid_type_error: validation?.required }
    ),
    dropOffLocation: dropOffAtDiffLocation
      ? z.object(
          {
            text: z.string(),
            value: z.string(),
            country: z.string(),
          },
          { invalid_type_error: validation?.required }
        )
      : z.nullable(z.object({})),
    pickUpDate: z.string(),
    pickUpTime: z.string(),
    dropOffDate: z.string(),
    dropOffTime: z.string(),
    // driverAge: z.string().optional(),
    aegeanCode: z
      .string()
      .regex(/^([0-9A-Z]{6})?$/, validation?.aegeanCode)
      .optional(),
    promos: z
      .array(
        z.object({
          type: z.string().optional(),
          code: z.string().optional(),
        })
      )
      .optional(),
    userId: z.string().optional(),
  })

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: currentValues
      ? {
          ...currentValues,
          pickUpLocation: currentValues.pickUpLocation
            ? {
                text: currentValues.pickUpLocation.text,
                value: currentValues.pickUpLocation.code,
                country: currentValues.pickUpLocation.country,
              }
            : '',
          dropOffLocation: currentValues.dropOffLocation
            ? {
                text: currentValues.dropOffLocation.text,
                value: currentValues.dropOffLocation.code,
                country: currentValues.dropOffLocation.country,
              }
            : '',
        }
      : {
          dropOffLocation: null,
        },
  })

  const onSubmit = async (data) => {
    const body = {
      ...data,
      pickUpLocation: {
        code: data.pickUpLocation.value,
        country: data.pickUpLocation.country,
        text: data.pickUpLocation.text,
      },
      dropOffLocation: {
        code: dropOffAtDiffLocation
          ? data.dropOffLocation.value
          : data.pickUpLocation.value,
        country: dropOffAtDiffLocation
          ? data.dropOffLocation.country
          : data.pickUpLocation.country,
        text: dropOffAtDiffLocation
          ? data.dropOffLocation.text
          : data.pickUpLocation.text,
      },
    }

    if (data.promos && showDiscount) {
      body.promos = data.promos.filter((promo) => promo.type && promo.code)
    } else {
      delete body.promos
    }
    setErrorMessages([])
    setLoading(true)

    try {
      const validationResponse = await postData(
        config?.endpoints?.homeSearchValidation +
          (mobileApp ? '?origin=app' : ''),
        body
      )
      if (validationResponse.hasError) {
        handleErrors(validationResponse.errorMessages)
        return
      }

      const response = await postData(
        config?.endpoints?.findCar + (mobileApp ? '?origin=app' : ''),
        body
      )

      if (response.hasError) {
        handleErrors(response.errorMessages)
        return
      }

      setOpenLoadingModal(true)
      //set step to results and reset filters
      if (!window.mobileAppData) {
        dispatch(setStep(constants.booking.pageNumber.vehicles))
        dispatch(setFilters({}))
      }
      setWaitForResultsLogos(response.data.waitForResultsLogos)
      window.location.href = response.data.redirectUrl
      handleSuccess()
    } catch (e) {
      handleErrors([dictionary.defaultErrorMessage])
    }
  }

  const handleErrors = (messages) => {
    setErrorMessages(
      messages?.length > 0 ? messages : [dictionary.defaultErrorMessage]
    )
    setLoading(false)
    setOpenErrorModal(true)
  }

  const handleSuccess = () => {
    setErrorMessages([])
    setLoading(false)
    setOpenErrorModal(false)
  }

  useEffect(() => {
    if (watch('pickUpDate') && watch('dropOffDate')) {
      const pickUp = new Date(watch('pickUpDate')).getTime()
      const dropOff = new Date(watch('dropOffDate')).getTime()
      if (pickUp > dropOff) {
        setValue('dropOffDate', '')
      } else if (pickUp === dropOff) {
        setIsSameDay(true)
      } else {
        setIsSameDay(false)
      }
    }
  }, [watch('pickUpDate'), watch('dropOffDate')])

  useEffect(() => {
    if (watch('pickUpDate') && !watch('pickUpTime')) {
      setValue('pickUpTime', '10:00', { shouldValidate: true })
    }
  }, [watch('pickUpDate'), watch('pickUpTime')])

  useEffect(() => {
    if (watch('dropOffDate')) {
      const currentDropOffTime = watch('dropOffTime')

      // Check if same day and pickup time is set
      if (isSameDay && watch('pickUpTime')) {
        const pickUpTimeIndex = timeOptions.indexOf(watch('pickUpTime'))
        const availableTimes = timeOptions.filter(
          (_, index) => index > pickUpTimeIndex
        )

        // Check if current dropoff time is invalid (not in available times or not set)
        const isInvalidTime =
          !currentDropOffTime || !availableTimes.includes(currentDropOffTime)

        if (isInvalidTime && availableTimes.length > 0) {
          // Use first available time after pickup
          setValue('dropOffTime', availableTimes[0], { shouldValidate: true })
        }
      } else if (!currentDropOffTime) {
        // Different days and no time set - use default
        setValue('dropOffTime', '10:00', { shouldValidate: true })
      }
    }
  }, [
    watch('dropOffDate'),
    watch('dropOffTime'),
    isSameDay,
    watch('pickUpTime'),
  ])

  const [localUserId, setLocalUserId] = useState(
    window.headerData?.milesNBonusAccount?.fFN || ''
  )

  useEffect(() => {
    const handleLoginSuccess = (event) => {
      setLocalUserId(event.detail.isLoggedIn ? event.detail.userId : '')
    }
    window.addEventListener('loggedIn', handleLoginSuccess)

    return () => {
      window.removeEventListener('loggedIn', handleLoginSuccess)
    }
  }, [])

  useEffect(() => {
    if (localUserId && userId === undefined) {
      setValue('userId', localUserId)
    } else if (userId) {
      setValue('userId', userId)
    }
  }, [localUserId, userId])

  const handleLogin = (e) => {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('openLoginModal'))
  }

  const handleClose = (e) => {
    if (handleCancel) handleCancel()
    else {
      setIsExpanded(false)
      setOpenFormModal(false)
    }
  }

  useEffect(() => {
    if (openFormModal) {
      document.documentElement.classList.add('scrollDisabled')
      document.body.classList.add('scrollDisabled')
      // Hide header when booking widget modal is open in mobile view
      if (mobileView) {
        document.documentElement.classList.add('hide-header')
      }
    } else {
      document.documentElement.classList.remove('scrollDisabled')
      document.body.classList.remove('scrollDisabled')
      document.documentElement.classList.remove('hide-header')
    }

    return () => {
      document.documentElement.classList.remove('scrollDisabled')
      document.body.classList.remove('scrollDisabled')
      document.documentElement.classList.remove('hide-header')
    }
  }, [openFormModal])

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)
  const dropOffDateRef = useRef(null)
  const dropOffTimeRef = useRef(null)

  const handlePickUpDateChange = (selectedDate) => {
    if (!selectedDate) {
      pickUpTimeRef.current?.open()
      return
    }

    const nextDay = new Date(selectedDate)
    nextDay.setDate(nextDay.getDate() + 1)
    const currentDropOffDate = watch('dropOffDate')

    // Only update drop-off date if it's not set or not beyond next day
    const shouldUpdateDropOff =
      !currentDropOffDate ||
      new Date(currentDropOffDate).getTime() <= nextDay.getTime()

    if (shouldUpdateDropOff) {
      setValue('dropOffDate', nextDay.toISOString().split('T')[0], {
        shouldValidate: true,
      })
    }

    pickUpTimeRef.current?.open()
  }

  const form = (
    <>
      {mobileView && (
        <div className="s-booking-widget__form-header">
          <h2 className="h2">{dictionary.findYourCar}</h2>
          <button
            type="button"
            className="btn p-0"
            onClick={handleClose}
            aria-label="Close booking form">
            <i className="icon icon-x"></i>
          </button>
        </div>
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={`${mobileView ? '' : 's-booking-widget__form'}`}
        autoComplete="off">
        <div className="mobile-section">
          <div className="s-booking-widget__inputs-grid ">
            <div className="s-booking-widget__location-fields">
              <SelectLocation
                name={'pickUpLocation'}
                label={
                  dropOffAtDiffLocation
                    ? dictionary.pickUpLabel
                    : dictionary.pickUpDropOffLabel
                }
                control={control}
                setValue={setValue}
                watch={watch}
                error={errors.pickUpLocation}
                handleFocus={(isFocused) => {
                  if (isFocused && !isExpanded) setIsExpanded(true)
                }}
                locationSearchApi={
                  config?.endpoints?.locationSearch +
                  (mobileApp ? '?origin=app' : '')
                }
                locationFindApi={
                  config?.endpoints?.locationFind +
                  (mobileApp ? '?origin=app' : '')
                }
                dictionary={dictionary}
              />
              {dropOffAtDiffLocation && (
                <SelectLocation
                  name={'dropOffLocation'}
                  label={dictionary.dropOffLabel}
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  error={errors.dropOffLocation}
                  locationSearchApi={
                    config?.endpoints?.locationSearch +
                    (mobileApp ? '?origin=app' : '')
                  }
                  locationFindApi={
                    config?.endpoints?.locationFind +
                    (mobileApp ? '?origin=app' : '')
                  }
                  dictionary={dictionary}
                />
              )}
            </div>
            {mobileView && (
              <Switch
                name={'dropOffSwitch'}
                label={dictionary.locationSwitch}
                checked={dropOffAtDiffLocation}
                onChange={setDropOffAtDiffLocation}
              />
            )}

            <div className="s-booking-widget__date-time">
              <DatePicker
                ref={pickUpDateRef}
                name="pickUpDate"
                label={dictionary.pickupDateLabel}
                control={control}
                setValue={setValue}
                disableBefore={new Date()}
                disableAfter={oneYearLater}
                error={errors.pickUpDate}
                dictionary={dictionary}
                defaultValue={currentValues?.pickUpDate}
                onChange={handlePickUpDateChange}
              />

              <SelectField
                ref={pickUpTimeRef}
                name={'pickUpTime'}
                control={control}
                label={dictionary.timeLabel}
                options={timeOptions?.map((option) => ({
                  value: option,
                  text: option,
                }))}
                error={errors.pickUpTime}
                isSelectTime
                onChange={() => dropOffDateRef.current?.open()}
              />
            </div>

            <div className="s-booking-widget__date-time">
              <DatePicker
                ref={dropOffDateRef}
                name="dropOffDate"
                label={dictionary.dropOffDateLabel}
                control={control}
                setValue={setValue}
                disableBefore={
                  watch('pickUpDate')
                    ? new Date(watch('pickUpDate'))
                    : new Date()
                }
                disableAfter={OneYearAndMonthLater}
                error={errors.dropOffDate}
                dictionary={dictionary}
                defaultValue={currentValues?.dropOffDate}
                onChange={() => dropOffTimeRef.current?.open()}
              />
              <SelectField
                ref={dropOffTimeRef}
                name={'dropOffTime'}
                control={control}
                label={dictionary.timeLabel}
                options={
                  watch('pickUpTime') && isSameDay
                    ? timeOptions
                        ?.filter(
                          (_, index) =>
                            index > timeOptions.indexOf(watch('pickUpTime'))
                        )
                        ?.map((option) => ({
                          value: option,
                          text: option,
                        }))
                    : timeOptions?.map((option) => ({
                        value: option,
                        text: option,
                      }))
                }
                error={errors.dropOffTime}
              />
            </div>
            {/* <SelectField
              name={'driverAge'}
              control={control}
              label={dictionary.driverAgeLabel}
              options={driverAgeOptions.map((option) => ({
                value: option,
                text: option,
              }))}
              error={errors.driverAge}
            /> */}
          </div>
        </div>

        {!mobileView && (
          <Switch
            name={'dropOffSwitch'}
            label={dictionary.locationSwitch}
            checked={dropOffAtDiffLocation}
            onChange={setDropOffAtDiffLocation}
          />
        )}

        <div
          className={`s-booking-widget__aegean ${
            isExpanded ? 's-booking-widget__aegean--show' : ''
          }`}>
          <div className="s-booking-widget__aegean__grid">
            <div className="s-booking-widget__aegean__field mobile-section">
              <span className="h4">{dictionary.aegeanTitle}</span>
              <div className="s-booking-widget__aegean__field--border">
                <TextField
                  label={dictionary.aegeanCodeLabel}
                  name="aegeanCode"
                  control={control}
                  error={errors.aegeanCode}
                  info={dictionary.aegeanCodeInfo}
                  maxLength={6}
                />
              </div>
            </div>
            {userId || localUserId ? (
              <button
                type="button"
                className="btn-clear s-booking-widget__aegean__field mobile-section"
                onClick={handleLogin}
                aria-label="Edit logged in user information">
                <span className="h4">{dictionary.loggedInBonusText}</span>
                <TextField
                  label={dictionary.userIdLabel}
                  name="userId"
                  control={control}
                  error={errors.userId}
                  readOnly
                />
              </button>
            ) : (
              <div className="mobile-section s-booking-widget__aegean__login-container">
                <div className="s-booking-widget__aegean__login">
                  <span className="h4">{dictionary.loggedOutBonusText}</span>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleLogin}
                    title={dictionary.loginBtn}>
                    {dictionary.loginBtn}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="s-booking-widget__promo mobile-section">
            {showDiscount && (
              <PromoFields
                control={control}
                handleNoPromo={() => setShowDiscount(false)}
                dictionary={dictionary}
                promoCodeTypeOptions={promoCodeTypeOptions}
                isMobile={mobileView}
              />
            )}
            <Switch
              name="discountSwitch"
              label={dictionary.promoCodeSwitch}
              checked={showDiscount}
              onChange={setShowDiscount}
            />
          </div>
        )}

        <div className="s-booking-widget__footer">
          <div className="s-booking-widget__logos">
            {logos?.map((logo) => (
              <img key={logo.name} src={logo.url} alt={logo.alt} />
            ))}
          </div>
          <div className="s-booking-widget__actions">
            {isExpanded && !mobileView && (
              <button
                type="button"
                className="btn s-booking-widget__cancel"
                onClick={handleClose}
                title={dictionary.cancelBtn}>
                {dictionary.cancelBtn}
              </button>
            )}
            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading}
              title={dictionary.findCarBtn}>
              {dictionary.findCarBtn}
            </button>
          </div>
        </div>
      </form>

      <LoadingResultsModal
        open={openLoadingModal}
        handleClose={() => setOpenLoadingModal(false)}
        logos={logos?.filter((logo) =>
          waitForResultsLogos?.includes(logo.name)
        )}
        text={dictionary.waitForResults}
      />
    </>
  )

  if (mobileApp)
    return (
      <>
        <div className="s-booking-widget__form">
          <SelectLocation
            name={'pickUpLocation'}
            label={
              dropOffAtDiffLocation
                ? dictionary.pickUpLabel
                : dictionary.pickUpDropOffLabel
            }
            control={control}
            setValue={setValue}
            watch={watch}
            error={errors.pickUpLocation}
            handleFocus={(isFocused) => {
              if (isFocused && !isExpanded) setIsExpanded(true)
            }}
            locationSearchApi={
              config?.endpoints?.locationSearch + '?origin=app'
            }
            locationFindApi={config?.endpoints?.locationFind + '?origin=app'}
            dictionary={dictionary}
          />
          {dropOffAtDiffLocation && (
            <SelectLocation
              name={'dropOffLocation'}
              label={dictionary.dropOffLabel}
              control={control}
              setValue={setValue}
              watch={watch}
              error={errors.dropOffLocation}
              locationSearchApi={
                config?.endpoints?.locationSearch + '?origin=app'
              }
              locationFindApi={config?.endpoints?.locationFind + '?origin=app'}
              dictionary={dictionary}
            />
          )}
          <Switch
            name={'dropOffSwitch'}
            label={dictionary.locationSwitch}
            checked={dropOffAtDiffLocation}
            onChange={setDropOffAtDiffLocation}
          />
          <button
            type="button"
            className="btn btn-primary btn-full-width"
            disabled={!watch('pickUpLocation')}
            onClick={() => {
              setOpenFormModal(true)
              setIsExpanded(true)
            }}
            title={dictionary.selectPickUpDate}>
            {dictionary.selectPickUpDate}
          </button>
        </div>
        <Modal
          open={editMode || openFormModal}
          onClose={handleClose}
          className={'s-booking-widget-modal-wrapper'}>
          <div className="s-booking-widget__modal">{form}</div>
        </Modal>
      </>
    )

  return (
    <div className="s-booking-widget">
      {mobileView ? (
        <>
          {!editMode && (
            <div className="page-container">
              <div className="s-booking-widget__form">
                <SelectLocation
                  name={'pickUpLocation'}
                  label={
                    dropOffAtDiffLocation
                      ? dictionary.pickUpLabel
                      : dictionary.pickUpDropOffLabel
                  }
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  error={errors.pickUpLocation}
                  handleFocus={(isFocused) => {
                    if (isFocused && !isExpanded) setIsExpanded(true)
                  }}
                  locationSearchApi={
                    config?.endpoints?.locationSearch +
                    (mobileApp ? '?origin=app' : '')
                  }
                  locationFindApi={
                    config?.endpoints?.locationFind +
                    (mobileApp ? '?origin=app' : '')
                  }
                  dictionary={dictionary}
                />
                {dropOffAtDiffLocation && (
                  <SelectLocation
                    name={'dropOffLocation'}
                    label={dictionary.dropOffLabel}
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    error={errors.dropOffLocation}
                    locationSearchApi={
                      config?.endpoints?.locationSearch +
                      (mobileApp ? '?origin=app' : '')
                    }
                    locationFindApi={
                      config?.endpoints?.locationFind +
                      (mobileApp ? '?origin=app' : '')
                    }
                    dictionary={dictionary}
                  />
                )}
                <Switch
                  name={'dropOffSwitch'}
                  label={dictionary.locationSwitch}
                  checked={dropOffAtDiffLocation}
                  onChange={setDropOffAtDiffLocation}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-full-width"
                  disabled={!watch('pickUpLocation')}
                  onClick={() => {
                    setOpenFormModal(true)
                    setIsExpanded(true)
                  }}
                  title={dictionary.selectPickUpDate}>
                  {dictionary.selectPickUpDate}
                </button>
              </div>
            </div>
          )}
          <Modal
            open={editMode || openFormModal}
            onClose={handleClose}
            hideBackdrop
            className="s-booking-widget-modal-wrapper">
            <div className="s-booking-widget__modal">{form}</div>
          </Modal>
        </>
      ) : (
        <div className="page-container">{form}</div>
      )}
      <ErrorModal
        errorMessages={errorMessages}
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        closeText={dictionary.cancel}
      />
    </div>
  )
}

// export default BookingWidget

// export default ({
//   handleCancel,
//   editMode = false,
//   mobileApp,
//   userId,
//   sticky,
// }) => {
//   const [data, setData] = useState()

//   useEffect(() => {
//     getData(window.bookingWidgetEndpoint).then((res) => setData(res.data))
//   }, [])

//   if (!data)
//     return (
//       <div className="d-flex justify-content-center py-4">
//         <CircularProgress />
//       </div>
//     )

//   return (
//     <BookingWidget
//       userId={userId}
//       editMode={editMode}
//       data={data}
//       handleCancel={handleCancel}
//       mobileApp={mobileApp}
//     />
//   )
// }

export default ({
  handleCancel,
  editMode = false,
  fleet = false,
  mobileApp,
  userId,
  sticky = false,
}) => {
  const [data, setData] = useState()
  const [isSticky, setIsSticky] = useState(false)
  const widgetRef = useRef(null)
  const originalPosition = useRef(null)

  useEffect(() => {
    getData(window.bookingWidgetEndpoint).then((res) => setData(res.data))
  }, [])

  useEffect(() => {
    if (!sticky || !data) return

    const handleScroll = () => {
      if (!widgetRef.current || window.innerWidth < 1920) return

      const widgetElement = widgetRef.current
      const rect = widgetElement.getBoundingClientRect()

      // Store original position on first scroll
      if (originalPosition.current === null) {
        originalPosition.current = rect.top + window.scrollY
      }

      const navbarHeight =
        document.querySelector('.s-header--sticky')?.offsetHeight
      const shouldBeSticky =
        window.scrollY >= originalPosition.current - navbarHeight

      if (shouldBeSticky && !isSticky) {
        setIsSticky(true)
      } else if (!shouldBeSticky && isSticky) {
        setIsSticky(false)
      }
    }

    const handleResize = () => {
      calculateInitialPosition()
      if (window.innerWidth < 1920) setIsSticky(false)
    }

    // Initial position calculation
    const calculateInitialPosition = () => {
      if (widgetRef.current) {
        const rect = widgetRef.current.getBoundingClientRect()
        originalPosition.current = rect.top + window.scrollY
      }
    }

    // Calculate position after data loads and component renders
    const timeoutId = setTimeout(calculateInitialPosition, 100)

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      clearTimeout(timeoutId)
    }
  }, [sticky, data, isSticky])

  const widgetElement = widgetRef.current

  if (!data)
    return (
      <div className="d-flex justify-content-center py-4">
        <CircularProgress />
      </div>
    )

  return (
    <>
      {/* Placeholder to prevent layout jump when sticky */}
      <div
        style={{
          height:
            isSticky && widgetElement
              ? `${widgetElement.offsetHeight}px`
              : '0px',
        }}
      />

      <div
        ref={widgetRef}
        style={{
          top: `${document.querySelector('.s-header--sticky')?.offsetHeight}px`,
        }}
        className={isSticky ? 's-booking-widget-sticky' : ''}>
        <BookingWidget
          userId={userId}
          editMode={editMode}
          fleet={fleet}
          data={data}
          handleCancel={handleCancel}
          mobileApp={mobileApp}
        />
      </div>
    </>
  )
}
